import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { DatepickerHelper } from '../../../shared/validators/date-picker-form-validators';
import { Company } from '@models/companies/company';
import { Document } from '@models/documents/document';
import { EntityChangeData } from '@models/documents/entity-change-data';
import { CompanyChangeData } from '@models/ui-models/company-change-data';
import { SelectOption } from '@models/ui-models/select-options';
import { ChangeDictionaryHelper } from '@models/ui-models/change-dictionary-helper.model';
import { IPDFSection } from '@models/ui-models/pdf-section.interface';
import { CompanyWithdrawLodgedDocumentChangeComponent } from '../../asic-forms/company/company-withdraw-lodged-document-change/company-withdraw-lodged-document-change.component';

export class CompanyWithdrawLodgedDocumentChange extends EntityChangeData {
  static override readonly $type = 'CompanyWithdrawLodgedDocumentChange';
  documentNumber: string;
  documentLodgementDate: Date;
  reason: DocumentWithdrawReason;
  duplicateDocumentNumber: string;
  withdrawalReasonDetails: string;

  override changeName = 'Lodged Document Withdrawal';

  constructor(data: Partial<CompanyWithdrawLodgedDocumentChange> = {}) {
    super(data);

    this.documentNumber = data.documentNumber ?? '';
    this.documentLodgementDate = DatepickerHelper.toDate(data.documentLodgementDate);
    this.duplicateDocumentNumber = data.duplicateDocumentNumber ?? '';
    this.withdrawalReasonDetails = data.withdrawalReasonDetails ?? '';

    if (typeof data.reason === 'string') {
      this.reason = DocumentWithdrawReasonOptions.find(option => option.value == data.reason)?.value as DocumentWithdrawReason ?? DocumentWithdrawReason.Duplicated;
    } else {
      this.reason = data.reason ?? DocumentWithdrawReason.Duplicated;
    }
  }

  override toDictionary(): { key: string; value: string; }[] {
    const dict = new ChangeDictionaryHelper();

    dict
      .add('Document Number', this.documentNumber)
      .addDate('Date of Lodgement', this.documentLodgementDate)

    if (typeof +this.reason === 'number') {
      dict.add('Reason', DocumentWithdrawReasonsDictionary[this.reason]);
    } else {
      dict.add('Reason', this.reason);
    }

    dict
      .addIfValueIsNotEmpty('Withdrawal Reason Details', this.withdrawalReasonDetails)
      .addIfValueIsNotEmpty('Document number of previously lodged document', this.duplicateDocumentNumber);

    return dict.dictionary;
  }

  override fillModal(modalRef: NgbModalRef, document: Document, actualCompany: Company): NgbModalRef {
    modalRef = this.fillModalWithCompany(modalRef, actualCompany, true);
    (modalRef.componentInstance as CompanyWithdrawLodgedDocumentChangeComponent).companyChangeData.documentId = document.documentId;
    return modalRef;
  }

  fillModalWithCompany(modalRef: NgbModalRef, company: Company, isEdit = false): NgbModalRef {
    const instance = (modalRef.componentInstance as CompanyWithdrawLodgedDocumentChangeComponent);

    instance.isEdit = isEdit;
    instance.companyChangeData = new CompanyChangeData(company);
    instance.formModel = structuredClone(this);

    return modalRef;
  }

  override prepareToRequest(): CompanyWithdrawLodgedDocumentChange {
    return {
      ...this,
      ...super.prepareToRequest(),
      documentLodgementDate: DatepickerHelper.buildDateString(this.documentLodgementDate),
    };
  }

  override prepareToPdf(): IPDFSection[] {
    const sectionDict = new ChangeDictionaryHelper();

    sectionDict
      .addSection('Document Details')
      .add('Document Number', this.documentNumber)
      .addDate('Date of lodgement of document being amended', this.documentNumber)

    sectionDict
      .addSection('Amendment Details')
      .add('Reason why this document should be withdrawn', DocumentWithdrawReasonsDictionary[this.reason])
      .add('Document number of previously lodged documents', this.duplicateDocumentNumber)
      .add('Specific Details', this.withdrawalReasonDetails)

    return sectionDict.sections;
  }
}

export enum DocumentWithdrawReason {
  Duplicated = 0,
  Incorrect = 1
}

export const DocumentWithdrawReasonsDictionary: Record<DocumentWithdrawReason, string> = {
  [DocumentWithdrawReason.Duplicated]: 'Duplicate - an identical document has been lodged already',
  [DocumentWithdrawReason.Incorrect]: 'Information on previously lodged document was subsequently found incorrect'
};

export const DocumentWithdrawReasonOptions: SelectOption[] = Object.entries(DocumentWithdrawReasonsDictionary).map(([value, label]) => ({
  label,
  value
}));
